/**
 * Axios HTTP Client with JWT Authentication
 * Handles automatic token refresh via interceptors
 */

import axios from 'axios';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for HTTP-only refresh token cookie
  timeout: 30000, // 30 second timeout
});

// Request interceptor - add JWT token to all requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('tw_access_token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Token refresh state management
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor - handle wrapped responses and token refresh on 401
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Handle wrapped API responses: { success: true, data: [...] }
    // BUT preserve paginated responses that have meta: { success, data, meta }
    if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data) {
      // Don't unwrap paginated responses - they need meta preserved
      if ('meta' in response.data) {
        return response;
      }
      return { ...response, data: response.data.data };
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Skip refresh logic for login/refresh endpoints
    const skipRefreshPaths = ['/login', '/refresh'];
    const requestPath = originalRequest.url || '';
    const shouldSkipRefresh = skipRefreshPaths.some(path => requestPath.includes(path));

    // Handle rate limiting (429)
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const minutes = retryAfter ? Math.ceil(Number(retryAfter) / 60) : 'a few';
      console.warn(`Rate limited. Retry after ${minutes} minutes.`);
      return Promise.reject(error);
    }

    // If error is 401 and we haven't retried yet and not a skip path
    if (error.response?.status === 401 && !originalRequest._retry && !shouldSkipRefresh) {
      if (isRefreshing) {
        // Queue this request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Call refresh endpoint (uses HTTP-only cookie automatically)
        const { data } = await axios.post(
          `${API_BASE_URL}/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = data.token;

        if (!newToken) {
          throw new Error('No token received from refresh');
        }

        // Update token in localStorage
        localStorage.setItem('tw_access_token', newToken);

        // Update header for original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        // Process queued requests
        processQueue(null, newToken);

        // Retry original request
        return apiClient(originalRequest);

      } catch (refreshError) {
        // Refresh failed - clear auth and redirect to login
        console.error('Token refresh failed:', refreshError);

        // Log detailed error information for debugging
        if (axios.isAxiosError(refreshError)) {
          console.error('Refresh error details:', {
            status: refreshError.response?.status,
            data: refreshError.response?.data,
            message: refreshError.message,
            cookiesSent: document.cookie.includes('refresh_token') ? 'Yes' : 'No (refresh_token cookie missing!)'
          });
        }

        processQueue(refreshError as Error, null);

        localStorage.removeItem('tw_access_token');
        localStorage.removeItem('tw_user_data');

        // Redirect to login (only if not already on login page)
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to handle API errors consistently
 */
export function handleApiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error || error.response?.data?.message || error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Helper to check if error is due to network issues
 */
export function isNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response;
}

export default apiClient;
